<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col sm="4">
        <b-card no-body class="bg-primary">
          <b-card-body class="pb-0">
            <h2 class="mb-0">{{ info.valid }}</h2>
            <p>{{ t("dashboard_valid-info").replace("{days}", info.days) }}</p>
          </b-card-body>
        </b-card>
      </b-col>
      <b-col sm="4">
        <b-card no-body class="bg-info">
          <b-card-body class="pb-0">
            <h2 class="mb-0">{{ info.limit }}</h2>
            <p>{{ t("dashboard_links-remaining") }}</p>
          </b-card-body>
        </b-card>
      </b-col>
      <b-col sm="4">
        <b-card no-body class="bg-warning">
          <b-card-body class="pb-0">
            <h2 class="mb-0">{{ info.api }}</h2>
            <p>
              {{ t("dashboard_your-api-key") }}
              <b-button
                class="float-right"
                variant="transparent p-0"
                right
                to="/docs"
              >
                {{ t("dashboard_how-to-use") }} <i class="icon-question"></i>
              </b-button>
            </p>
          </b-card-body>
        </b-card>
      </b-col>
      <b-col sm="12">
        <b-card border-variant="primary">
          <b-card-body class="pb-0">
            <div v-if="invalid !== ''">
              <b-alert variant="danger" show>{{
                t("dashboard_this-links-are-invalid")
              }}</b-alert>
              <b-form-group>
                <b-form-textarea
                  :rows="3"
                  readonly
                  :max-rows="6"
                  v-model="invalid"
                >
                </b-form-textarea>
              </b-form-group>
              <hr class="my-4" />
            </div>
            <b-row>
              <b-col sm="9">
                <b-form-group>
                  <b-form-input
                    :placeholder="t('dashboard_project-name-optional')"
                    type="text"
                    v-model="form.project"
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col sm="3">
                <b-form-group>
                  <b-form-select
                    :plain="true"
                    v-model="form.days"
                    :options="dripfeed"
                  >
                  </b-form-select>
                </b-form-group>
              </b-col>
            </b-row>
            <b-form-group>
              <b-form-textarea
                :placeholder="t('dashboard_paste-or-file')"
                :rows="3"
                :max-rows="6"
                v-model="form.links"
              >
              </b-form-textarea>
            </b-form-group>
            <b-form-group>
              <b-form-file :plain="true" @change="onFileChanged"></b-form-file>
            </b-form-group>
            <b-button
              type="submit"
              block
              variant="primary"
              :disabled="!canSendLinks || blocked"
              @click="onLinksSubmit"
              ><span v-if="!blocked">{{ t("global_submit") }}</span
              ><i class="fa fa-spinner" v-else></i
            ></b-button>
          </b-card-body>
        </b-card>
      </b-col>
      <b-col sm="12">
        <b-card border-variant="primary">
          <h1 class="text-center mb-4">{{ t("dashboard_your-projects") }}</h1>
          <b-table
            v-if="projects.all"
            responsive="sm"
            :items="projects.all"
            :fields="captions"
            fixed
            :current-page="currentPage.all"
            :per-page="perPage"
          >
            <template slot="status" slot-scope="data">
              <b-progress :max="100" show-value>
                <b-progress-bar
                  :value="data.item.status1"
                  variant="success"
                  animated
                  :style="{ color: 'black' }"
                >
                  {{
                    t("dashboard_percent-complete1").replace(
                      "{a1}",
                      data.item.status1
                    )
                  }}
                </b-progress-bar>
              </b-progress>
              <b-progress :max="100" show-value class="mt-4">
                <b-progress-bar
                  :value="data.item.status2"
                  variant="success"
                  animated
                  :style="{ color: 'black' }"
                >
                  {{
                    t("dashboard_percent-complete2").replace(
                      "{a1}",
                      data.item.status2
                    )
                  }}
                </b-progress-bar>
              </b-progress>
            </template>
            <template slot="actions" slot-scope="data">
              <b-button
                variant="primary"
                size="sm"
                class="mr-1"
                @click="onSendAgain(data.item.id)"
              >
                {{ t("dashboard_send-again") }}
              </b-button>
              <b-button
                variant="primary"
                size="sm"
                class="mr-1"
                :href="'/api/media/links/' + data.item.filename"
                target="_blank"
              >
                {{ t("dashboard_show-links") }}
              </b-button>
              <b-button
                variant="danger"
                size="sm"
                class="mr-1"
                style="margin-top: 10px"
                @click="onDelete(data.item.id)"
              >
                {{ t("dashboard_delete-links-whole") }}
              </b-button>
            </template>
          </b-table>
          <nav>
            <b-pagination
              :total-rows="totalRows('all')"
              :per-page="perPage"
              v-model="currentPage.all"
              :prev-text="t('global_prev')"
              :next-text="t('global_next')"
              hide-goto-end-buttons
            />
          </nav>
        </b-card>
      </b-col>
      <b-col sm="12">
        <b-card border-variant="primary">
          <h1 class="text-center mb-4">
            {{ t("dashboard_dripfeed-projects") }}
          </h1>
          <b-table
            v-if="projects.dripfeed"
            responsive="sm"
            :items="projects.dripfeed"
            :fields="captions"
            fixed
            :current-page="currentPage.dripfeed"
            :per-page="perPage"
          >
            <template slot="status" slot-scope="data">
              <b-progress :max="100" show-value>
                <b-progress-bar
                  :value="data.item.status1"
                  variant="success"
                  animated
                  :style="{ color: 'black' }"
                >
                  {{
                    t("dashboard_percent-complete1").replace(
                      "{a1}",
                      data.item.status1
                    )
                  }}
                </b-progress-bar>
              </b-progress>
              <b-progress :max="100" show-value class="mt-4">
                <b-progress-bar
                  :value="data.item.status2"
                  variant="success"
                  animated
                  :style="{ color: 'black' }"
                >
                  {{
                    t("dashboard_percent-complete2").replace(
                      "{a1}",
                      data.item.status2
                    )
                  }}
                </b-progress-bar>
              </b-progress>
            </template>
            <template slot="actions" slot-scope="data">
              <b-button
                variant="primary"
                size="sm"
                class="mr-1"
                @click="onSendAgain(data.item.id)"
              >
                {{ t("dashboard_send-again") }}
              </b-button>
              <b-button
                variant="primary"
                size="sm"
                class="mr-1"
                :href="'/api/media/links/' + data.item.filename"
                target="_blank"
              >
                {{ t("dashboard_show-links") }}
              </b-button>
              <b-button
                variant="danger"
                size="sm"
                class="mr-1"
                style="margin-top: 10px"
                @click="onDelete(data.item.id)"
              >
                {{ t("dashboard_delete-links-this") }}
              </b-button>
            </template>
          </b-table>
          <nav>
            <b-pagination
              :total-rows="totalRows('dripfeed')"
              :per-page="perPage"
              v-model="currentPage.dripfeed"
              :prev-text="t('global_prev')"
              :next-text="t('global_next')"
              hide-goto-end-buttons
            />
          </nav>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import Vue from "vue";

export default {
  name: "dashboard",
  data: () => {
    return {
      invalid: "",
      form: {
        project: "",
        days: "",
        links: "",
      },
      dripfeed: [],
      blocked: false,
      info: {
        api: "",
        limit: "",
        valid: "",
        days: "",
      },
      currentPage: {
        all: 1,
        dripfeed: 1,
      },
      perPage: 25,
      captions: [
        { key: "id", sortable: true },
        { key: "project", sortable: true },
        { key: "count", sortable: true },
        { key: "createdAt", sortable: true },
        { key: "status", sortable: true },
        { key: "actions", sortable: false },
      ],
      projects: {
        all: undefined,
        dripfeed: undefined,
      },
    };
  },
  methods: {
    loadDashboard() {
      const that = this;

      Vue.axios({
        method: "GET",
        url: "/dashboard",
        mode: "no-cors",
        withCredentials: true,
        credentials: "same-origin",
      })
        .then((response) => {
          if (response.data.status) {
            that.info = response.data.info;

            if (that.info.days <= 0) that.info.days = 0;

            that.projects = response.data.projects;

            if (that.info.valid == null || that.info.days <= 0)
              that.info.valid = this.$translate.text(
                "dashboard_inactive-account"
              );

            that.dripfeed = [this.$translate.text("dashboard_how-many-days")];

            if (response.data.dripfeed > 0)
              that.form.days = response.data.dripfeed;

            for (let i = 1; i <= 30; i++) that.dripfeed.push(i);
          } else {
            Vue.swal(
              this.$translate.text("global_an-unexpected-error-occurred"),
              "",
              "warning"
            );
          }
        })
        .catch(() => {
          Vue.swal(
            this.$translate.text("global_an-unexpected-error-occurred"),
            "",
            "warning"
          );
        });
    },
    onLinksSubmit() {
      if (this.form.links.trim() === "") {
        Vue.swal(
          this.$translate.text("dashboard_you-must-enter-your-links"),
          "",
          "warning"
        );
      } else {
        this.blocked = true;

        const that = this;

        Vue.axios({
          method: "POST",
          url: "/link/add",
          data: {
            project: this.form.project,
            links: this.form.links.split(/\r?\n/),
            days:
              this.form.days === this.$translate.text("dashboard_how-many-days")
                ? 1
                : this.form.days,
          },
          mode: "no-cors",
          withCredentials: true,
          credentials: "same-origin",
        })
          .then((response) => {
            if (response.data.status) {
              if (response.data.count > 0) {
                Vue.swal(
                  this.$translate.text("dashboard_links-sent"),
                  "(" + response.data.count + ")",
                  "success"
                );
              } else {
                Vue.swal(
                  this.$translate.text("dashboard_all-invalid"),
                  "",
                  "warning"
                );
              }

              that.invalid = response.data.invalid
                ? response.data.invalid.join("\n")
                : "";
              that.blocked = false;

              that.loadDashboard();
              that.form.days = this.$translate.text("dashboard_how-many-days");
              that.form.project = "";
              that.form.links = "";
            } else {
              Vue.swal(
                this.$translate.text(response.data.message),
                "",
                "warning"
              );
              that.blocked = false;
            }
          })
          .catch(() => {
            Vue.swal(
              this.$translate.text("global_an-unexpected-error-occurred"),
              "",
              "warning"
            );
            that.blocked = false;
          });
      }
    },
    onFileChanged(ev) {
      const file = ev.target.files[0];
      const reader = new FileReader();

      const that = this;

      reader.onload = (e) => {
        that.form.links = e.target.result;
        ev.target.value = "";
      };

      try {
        reader.readAsText(file);
      } catch (err) {
        Vue.swal(
          this.$translate.text("dashboard_incorrect-file"),
          "",
          "warning"
        );
      }
    },
    onSendAgain(id) {
      const that = this;

      Vue.axios({
        method: "GET",
        url: "/project/" + id + "/links",
        mode: "no-cors",
        withCredentials: true,
        credentials: "same-origin",
      })
        .then((response) => {
          if (response.data.status) {
            that.form.links = response.data.links;
            window.scrollTo(0, 0);
          } else {
            Vue.swal(
              this.$translate.text(
                response.data.message
                  ? response.data.message
                  : "global_an-unexpected-error-occurred"
              ),
              "",
              "warning"
            );
          }
        })
        .catch(() => {
          Vue.swal(
            this.$translate.text("global_an-unexpected-error-occurred"),
            "",
            "warning"
          );
        });
    },
    onDelete(id) {
      const that = this;

      Vue.axios({
        method: "POST",
        url: "/project/" + id + "/delete",
        mode: "no-cors",
        withCredentials: true,
        credentials: "same-origin",
      })
        .then((response) => {
          if (response.data.status) {
            Vue.swal(
              this.$translate.text("dashboard_project-deleted"),
              "",
              "success"
            );
            that.loadDashboard();
          } else {
            Vue.swal(
              this.$translate.text(
                response.data.message
                  ? response.data.message
                  : "global_an-unexpected-error-occurred"
              ),
              "",
              "warning"
            );
          }
        })
        .catch(() => {
          Vue.swal(
            this.$translate.text("global_an-unexpected-error-occurred"),
            "",
            "warning"
          );
        });
    },
    totalRows(name) {
      return this.projects[name] ? this.projects[name].length : 0;
    },
  },
  computed: {
    canSendLinks() {
      return (
        (this.info.limit > 0 && this.info.days > 0) || this.$store.state.isAdmin
      );
    },
  },
  beforeMount() {
    this.form.days = this.$translate.text("dashboard_how-many-days");
    this.dripfeed.push(this.$translate.text("dashboard_how-many-days"));

    this.captions[1].label = this.$translate.text("global_project");
    this.captions[2].label = this.$translate.text("global_count");
    this.captions[3].label = this.$translate.text("global_date");
    this.captions[4].label = this.$translate.text("global_status");
    this.captions[5].label = this.$translate.text("global_actions");

    this.loadDashboard();
  },
};
</script>
